interface Props {
  darkMode: boolean;
  onClose: () => void;
}

const SHORTCUTS = [
  {
    category: 'Navigation',
    icon: 'ri-compass-3-line',
    color: 'text-teal-500',
    keys: [
      { keys: ['Tab'], desc: 'Switch between panel tabs' },
      { keys: ['Esc'], desc: 'Close any open modal' },
    ],
  },
  {
    category: 'Tutorial Player',
    icon: 'ri-play-circle-line',
    color: 'text-cyan-500',
    keys: [
      { keys: ['Space'], desc: 'Play / Pause current chapter' },
      { keys: ['←'], desc: 'Previous chapter' },
      { keys: ['→'], desc: 'Next chapter' },
    ],
  },
  {
    category: 'Projects',
    icon: 'ri-folder-line',
    color: 'text-violet-500',
    keys: [
      { keys: ['Ctrl', 'S'], desc: 'Open Save Project dialog' },
      { keys: ['Ctrl', 'O'], desc: 'Open My Projects panel' },
    ],
  },
  {
    category: 'View',
    icon: 'ri-eye-line',
    color: 'text-amber-500',
    keys: [
      { keys: ['Ctrl', 'D'], desc: 'Toggle Dark / Light mode' },
      { keys: ['Ctrl', 'Shift', 'F'], desc: 'Toggle fullscreen canvas' },
    ],
  },
];

export default function KeyboardShortcutsCard({ darkMode, onClose }: Props) {
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const kbdBg = darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700';

  return (
    <div
      className={`fixed bottom-24 right-6 z-50 w-80 rounded-2xl shadow-2xl border ${border} ${bg} overflow-hidden`}
      style={{ maxHeight: '80vh' }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${border}`}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
            <i className="ri-keyboard-line text-sm text-white"></i>
          </div>
          <span className={`text-sm font-bold ${textPrimary}`}>Keyboard Shortcuts</span>
        </div>
        <button
          onClick={onClose}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors cursor-pointer ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {/* Shortcut groups */}
      <div className="overflow-y-auto p-3 space-y-3" style={{ maxHeight: 'calc(80vh - 56px)' }}>
        {SHORTCUTS.map((group) => (
          <div key={group.category} className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
            <div className={`flex items-center gap-2 px-3 py-2 border-b ${border}`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${group.icon} text-sm ${group.color}`}></i>
              </div>
              <span className={`text-xs font-semibold uppercase tracking-wider ${textSecondary}`}>
                {group.category}
              </span>
            </div>
            <div className="p-2 space-y-1.5">
              {group.keys.map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3">
                  <span className={`text-xs ${textSecondary} flex-1`}>{shortcut.desc}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {shortcut.keys.map((k, ki) => (
                      <span key={ki} className="flex items-center gap-1">
                        <kbd className={`px-2 py-0.5 rounded border text-xs font-mono font-semibold shadow-sm ${kbdBg}`}>
                          {k}
                        </kbd>
                        {ki < shortcut.keys.length - 1 && (
                          <span className={`text-xs ${textSecondary}`}>+</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <p className={`text-xs text-center ${textSecondary} pb-1`}>
          <i className="ri-information-line mr-1"></i>
          Shortcuts work when no input is focused
        </p>
      </div>
    </div>
  );
}
